import { useState } from "react";
import { Badge } from "@/components/ui/badge.jsx";
import { getStatusBadgeClass } from "@/lib/status-utils.js";
import { formatDateTimeWithSeconds } from "@/lib/datetime-utils.js";
import { cn } from "@/lib/utils.js";
import { MapPin, Clock, ExternalLink, Box, Activity, Info } from "lucide-react";
import OptimizationVitals from "./optimization-vitals.jsx";
import OptimizationCylinderSummary from "./optimization-cylinder-summary.jsx";

const OPTIMIZATION_TABS = [
  { id: "vitals", label: "Vitals", icon: Activity },
  { id: "cylinder-summary", label: "Cylinder Summary", icon: Box },
];

function DateTimeMetaLine({ value }) {
  if (!value) return null;
  const formatted = formatDateTimeWithSeconds(value);
  const text = formatted !== "N/A" ? formatted : value;
  return (
    <span className='flex items-center gap-1.5'>
      <Clock className='h-4 w-4 shrink-0 text-muted-foreground' />
      <span className='border-b border-dotted border-border pb-0.5'>
        {text}
      </span>
    </span>
  );
}

/**
 * Header, tabs, and tab panels for the optimization experience when data exists.
 */
export default function OptimizationDashboard({
  normalized,
  enalysisViewUrl,
  operatingConditionsLoading = false,
  equipmentInfoLoading = false,
  compNum,
  compId,
}) {
  const [activeTab, setActiveTab] = useState("vitals");
  const displayLocation = normalized?.legalLocation ?? "";
  const displayAssetId = normalized?.detechtionCompressorNum ?? "";
  const unitConfigurationImperial = normalized?.unitConfigurationImperial;
  const compressorName = normalized?.compressorName;
  const lastReportDate = normalized?.lastReportDate;
  const { severityLevel } = normalized;

  const isSeverityWarning = severityLevel && /warning/i.test(severityLevel);
  const severityBadge = severityLevel && (
    <Badge
      variant='status'
      className={getStatusBadgeClass(
        isSeverityWarning ? "alerted" : "offline",
        "inline-flex items-center gap-1.5"
      )}>
      <Info
        className={cn(
          "h-4 w-4 shrink-0",
          isSeverityWarning
            ? "text-amber-600 dark:text-amber-400"
            : "text-red-600 dark:text-red-400"
        )}
        strokeWidth={2.5}
      />
      <span>{severityLevel}</span>
    </Badge>
  );

  const displayName = compressorName ?? displayAssetId;
  const assetLocationLine =
    displayName && displayLocation
      ? `${displayName}: ${displayLocation}`
      : displayLocation || displayName || "";

  return (
    <div className='space-y-6'>
      <header className='rounded-lg border border-border bg-card px-2 pt-2'>
        <div className='flex flex-wrap items-center justify-between gap-3 pb-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <h1 className='text-2xl font-bold text-foreground'>
              Compressor Optimization
            </h1>
            {displayAssetId && (
              <span className='inline-flex items-center rounded-md bg-muted px-2.5 py-0.5 text-sm font-medium text-muted-foreground'>
                #{displayAssetId}
              </span>
            )}
            {unitConfigurationImperial && (
              <span className='inline-flex items-center rounded-md bg-muted px-2.5 py-0.5 text-sm font-medium text-muted-foreground'>
                {unitConfigurationImperial}
              </span>
            )}
          </div>
          {severityBadge}
        </div>
        <div className='flex flex-wrap items-center gap-x-6 gap-y-1 pb-4 text-sm text-muted-foreground'>
          {assetLocationLine && (
            <span className='flex items-center gap-1.5'>
              <MapPin className='h-4 w-4 shrink-0 text-muted-foreground' />
              <span className='border-b border-dotted border-border pb-0.5'>
                {assetLocationLine}
              </span>
            </span>
          )}
          <DateTimeMetaLine value={lastReportDate} />
          {enalysisViewUrl && (
            <a
              href={enalysisViewUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-1.5 font-medium text-brand-cyan hover:underline'>
              <ExternalLink className='h-4 w-4 shrink-0 text-brand-cyan' />
              View in Enalysis
            </a>
          )}
        </div>
        <nav className='flex gap-0 border-t border-border -mx-2 px-2'>
          {OPTIMIZATION_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type='button'
              onClick={() => setActiveTab(id)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === id
                  ? "border-primary text-foreground bg-muted"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}>
              <Icon className='h-4 w-4' />
              {label}
            </button>
          ))}
        </nav>
      </header>

      {activeTab === "vitals" && (
        <OptimizationVitals
          normalized={normalized}
          operatingConditionsLoading={operatingConditionsLoading}
          equipmentInfoLoading={equipmentInfoLoading}
        />
      )}

      {activeTab === "cylinder-summary" && (
        <OptimizationCylinderSummary compNum={compNum} compId={compId} />
      )}
    </div>
  );
}
